import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Calendar, MapPin, Plus, CheckCircle, XCircle, AlertCircle, Pencil, Search, X, ChevronUp, ChevronDown, ExternalLink, RefreshCw, BarChart3
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import Layout from "../components/Layout";
import DynamicFormRenderer from "../components/DynamicFormRenderer";
import { API, useProject } from "../App";
import { toast } from "sonner";
import { usePermissions } from "../hooks/usePermissions";
import { useUrlFilters } from "../hooks/useUrlFilters";
import { useScrollRestore } from "../hooks/useScrollRestore";
import { DateRangePicker } from "../components/DateRangePicker";
import SavedViewsBar from "../components/SavedViewsBar";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled", color: "bg-blue-100 text-blue-700" },
  { value: "completed", label: "Completed", color: "bg-emerald-100 text-emerald-700" },
  { value: "rescheduled", label: "Rescheduled", color: "bg-amber-100 text-amber-700" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-700" },
  { value: "no_show", label: "No Show", color: "bg-slate-100 text-slate-600" },
];

const SCORE_OPTIONS = [
  { value: "hot", label: "Hot", color: "bg-red-100 text-red-700" },
  { value: "warm", label: "Warm", color: "bg-orange-100 text-orange-700" },
  { value: "mild", label: "Mild", color: "bg-yellow-100 text-yellow-700" },
  { value: "cold", label: "Cold", color: "bg-blue-100 text-blue-700" },
  { value: "nurture", label: "Nurture", color: "bg-slate-100 text-slate-600" },
];

const getStatusBadge = (status) => STATUS_OPTIONS.find(s => s.value === status) || { label: status, color: "bg-slate-100 text-slate-600" };
const getScoreBadge = (score) => SCORE_OPTIONS.find(s => s.value === score) || null;

const SiteVisitsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canEdit, canViewAllLeads } = usePermissions();
  const { selectedProjectId } = useProject();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);

  const [filters, setFilters, resetFilters, activeFilterCount] = useUrlFilters({
    status: "",
    sales_rep_id: "",
    project: "",
    score: "",
    search: "",
    rescheduled: false,
    date_from: "",
    date_to: "",
  });
  const dateRange = { from: filters.date_from, to: filters.date_to };
  const setDateRange = (next) => setFilters({
    date_from: typeof next === "function" ? next(dateRange).from : next.from,
    date_to: typeof next === "function" ? next(dateRange).to : next.to,
  });
  useScrollRestore([visits.length]);
  const [sortConfig, setSortConfig] = useState({ key: "scheduled_date", dir: "desc" });

  const [selectedVisit, setSelectedVisit] = useState(null);
  const [feedback, setFeedback] = useState({ post_visit_feedback: "", interest_level: "", status: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [rescheduleVisit, setRescheduleVisit] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ scheduled_date: "", reason: "" });
  const [revisitVisit, setRevisitVisit] = useState(null);
  const [revisitForm, setRevisitForm] = useState({ scheduled_date: "", project: "", pre_visit_notes: "" });
  const [visitAnalytics, setVisitAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchVisits();
    fetchAnalytics();
  }, [filters.status, filters.sales_rep_id, filters.project, dateRange, selectedProjectId]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`);
      setUsers(res.data.filter(u => u.status === "active"));
    } catch {}
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/visits/projects`);
      setProjects(res.data || []);
    } catch {}
  };

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.sales_rep_id) params.append("sales_rep_id", filters.sales_rep_id);
      if (filters.project) params.append("project", filters.project);
      else if (selectedProjectId !== "all") params.append("project_id", selectedProjectId);
      if (dateRange.from) {
        params.append("date_from_utc", new Date(`${dateRange.from}T00:00:00`).toISOString());
      }
      if (dateRange.to) {
        params.append("date_to_utc", new Date(`${dateRange.to}T23:59:59`).toISOString());
      }
      const response = await axios.get(`${API}/visits?${params.toString()}`);
      setVisits(response.data);
    } catch {
      toast.error("Failed to fetch site visits");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append("date_from_utc", new Date(`${dateRange.from}T00:00:00`).toISOString());
      if (dateRange.to) params.append("date_to_utc", new Date(`${dateRange.to}T23:59:59`).toISOString());
      const res = await axios.get(`${API}/visits/analytics?${params.toString()}`);
      setVisitAnalytics(res.data);
    } catch {}
  };

  const handleUpdateVisit = async () => {
    if (!selectedVisit) return;
    
    // When completing, require post-visit analysis
    if (feedback.status === "completed") {
      const pvd = feedback.postVisitData || {};
      if (!pvd.interest_level || !pvd.buyer_mood) {
        toast.error("Interest Level and Buyer Mood are required for completion");
        return;
      }
      // Merge post-visit data into the update payload
      const payload = {
        status: "completed",
        interest_level: pvd.interest_level,
        post_visit_feedback: pvd.notes || "",
        post_visit_analysis: pvd,
      };
      try {
        await axios.put(`${API}/visits/${selectedVisit.visit_id}`, payload);
        toast.success("Visit completed with analysis");
        setSelectedVisit(null);
        setFeedback({ post_visit_feedback: "", interest_level: "", status: "" });
        fetchVisits();
      } catch { toast.error("Failed to update visit"); }
      return;
    }
    
    try {
      await axios.put(`${API}/visits/${selectedVisit.visit_id}`, feedback);
      toast.success("Visit updated");
      setSelectedVisit(null);
      setFeedback({ post_visit_feedback: "", interest_level: "", status: "" });
      fetchVisits();
    } catch { toast.error("Failed to update visit"); }
  };

  const saveEditVisit = async () => {
    if (!editForm) return;
    try {
      await axios.put(`${API}/visits/${editForm.visit_id}`, {
        scheduled_date: editForm.scheduled_date,
        project: editForm.project,
        pre_visit_notes: editForm.pre_visit_notes,
      });
      toast.success("Visit updated");
      setEditOpen(false);
      fetchVisits();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const handleReschedule = async () => {
    if (!rescheduleVisit || !rescheduleForm.scheduled_date) {
      toast.error("Please select a new date and time");
      return;
    }
    try {
      await axios.put(`${API}/visits/${rescheduleVisit.visit_id}`, {
        scheduled_date: new Date(rescheduleForm.scheduled_date).toISOString(),
        reschedule_reason: rescheduleForm.reason,
        status: "scheduled",
      });
      toast.success("Visit rescheduled");
      setRescheduleVisit(null);
      setRescheduleForm({ scheduled_date: "", reason: "" });
      fetchVisits();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reschedule"); }
  };

  const handleScheduleRevisit = async () => {
    if (!revisitVisit || !revisitForm.scheduled_date) {
      toast.error("Please select a date and time for the revisit");
      return;
    }
    try {
      const payload = {
        lead_id: revisitVisit.lead_id,
        scheduled_date: new Date(revisitForm.scheduled_date).toISOString(),
        visit_type: "revisit",
        project: revisitForm.project || revisitVisit.project,
        pre_visit_notes: revisitForm.pre_visit_notes || "",
      };
      if (revisitForm.visit_time) payload.visit_time = revisitForm.visit_time;
      if (revisitForm.revisit_reason) payload.revisit_reason = revisitForm.revisit_reason;
      await axios.post(`${API}/visits`, payload);
      toast.success("Revisit scheduled — lead stage updated to Revisit");
      setRevisitVisit(null);
      setRevisitForm({});
      fetchVisits();
      fetchAnalytics();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule revisit"); }
  };

  // Client-side filtering for score and search
  const filteredVisits = visits.filter(v => {
    if (filters.score && v.lead_score !== filters.score) return false;
    if (filters.rescheduled && !(v.reschedule_count > 0)) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const match = (v.lead_name || "").toLowerCase().includes(q)
        || (v.project || "").toLowerCase().includes(q)
        || (v.lead_phone || "").toLowerCase().includes(q)
        || (v.lead_email || "").toLowerCase().includes(q)
        || (v.lead_id || "").toLowerCase().includes(q)
        || (v.rep_name || "").toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  // Sort
  const sortedVisits = [...filteredVisits].sort((a, b) => {
    const { key, dir } = sortConfig;
    let aVal = a[key] || "";
    let bVal = b[key] || "";
    if (key === "scheduled_date") {
      aVal = new Date(aVal).getTime() || 0;
      bVal = new Date(bVal).getTime() || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return dir === "asc" ? -1 : 1;
    if (aVal > bVal) return dir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <ChevronUp className="w-3 h-3 text-slate-300" />;
    return sortConfig.dir === "asc"
      ? <ChevronUp className="w-3 h-3 text-slate-700" />
      : <ChevronDown className="w-3 h-3 text-slate-700" />;
  };

  const scheduledCount = visits.filter(v => v.status === "scheduled").length;
  const completedCount = visits.filter(v => v.status === "completed").length;
  const cancelledCount = visits.filter(v => ["cancelled", "no_show"].includes(v.status)).length;

  const rescheduledCount = visits.filter(v => v.reschedule_count > 0).length;

  const hasActiveFilters = filters.status || filters.sales_rep_id || filters.project || filters.score || dateRange.from || filters.search || filters.rescheduled;

  const clearAllFilters = () => {
    setFilters({ status: "", sales_rep_id: "", project: "", score: "", search: "", rescheduled: false });
    setDateRange({ from: "", to: "" });
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-5" data-testid="site-visits-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">Site Visits</h1>
            <p className="text-slate-500 mt-1 text-sm">{filteredVisits.length} of {visits.length} visits</p>
          </div>
          <Button
            variant={showAnalytics ? "default" : "outline"}
            size="sm"
            onClick={() => setShowAnalytics(!showAnalytics)}
            data-testid="toggle-analytics"
          >
            <BarChart3 className="w-4 h-4 mr-1.5" /> {showAnalytics ? "Hide Analytics" : "Show Analytics"}
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters(f => ({ ...f, status: "", rescheduled: false }))} data-testid="total-stat-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total</p>
                <p className="text-xl font-bold text-slate-900 font-['Manrope']">{visits.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${filters.status === "scheduled" && !filters.rescheduled ? "ring-2 ring-blue-400" : ""}`} onClick={() => setFilters(f => ({ ...f, status: f.status === "scheduled" ? "" : "scheduled", rescheduled: false }))} data-testid="scheduled-stat-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Scheduled</p>
                <p className="text-xl font-bold text-blue-600 font-['Manrope']">{scheduledCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${filters.status === "completed" && !filters.rescheduled ? "ring-2 ring-emerald-400" : ""}`} onClick={() => setFilters(f => ({ ...f, status: f.status === "completed" ? "" : "completed", rescheduled: false }))} data-testid="completed-stat-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Completed</p>
                <p className="text-xl font-bold text-emerald-600 font-['Manrope']">{completedCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${(filters.status === "cancelled" || filters.status === "no_show") && !filters.rescheduled ? "ring-2 ring-red-400" : ""}`} onClick={() => setFilters(f => ({ ...f, status: f.status === "cancelled" ? "" : "cancelled", rescheduled: false }))} data-testid="cancelled-stat-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Cancelled</p>
                <p className="text-xl font-bold text-red-600 font-['Manrope']">{cancelledCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${filters.rescheduled ? "ring-2 ring-amber-400" : ""}`}
            onClick={() => setFilters(f => ({ ...f, rescheduled: !f.rescheduled, status: "" }))}
            data-testid="rescheduled-stat-card"
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Rescheduled</p>
                <p className="text-xl font-bold text-amber-600 font-['Manrope']">{rescheduledCount}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Saved Views */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-3">
            <SavedViewsBar
              path="/site-visits"
              currentQuery={searchParams.toString()}
              activeFilterCount={activeFilterCount}
            />
          </CardContent>
        </Card>

        {/* Analytics Panel */}
        {showAnalytics && visitAnalytics && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Rates */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Visit Metrics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Completion Rate</span>
                    <span className="text-sm font-bold text-emerald-600">{visitAnalytics.completion_rate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${visitAnalytics.completion_rate}%` }}></div>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-sm text-slate-500">Reschedule Rate</span>
                    <span className="text-sm font-bold text-amber-600">{visitAnalytics.reschedule_rate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${Math.min(visitAnalytics.reschedule_rate, 100)}%` }}></div>
                  </div>
                  <div className="flex justify-between items-center pt-2 text-xs text-slate-400">
                    <span>Total reschedule events: {visitAnalytics.total_reschedule_count}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interest Breakdown */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Interest Level (Completed)</h3>
                <div className="space-y-2.5">
                  {[
                    { key: "high", label: "High Interest", color: "bg-emerald-500" },
                    { key: "medium", label: "Medium Interest", color: "bg-amber-500" },
                    { key: "low", label: "Low Interest", color: "bg-slate-400" },
                  ].map(item => {
                    const count = visitAnalytics.interest_breakdown?.[item.key] || 0;
                    const total = Object.values(visitAnalytics.interest_breakdown || {}).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round(count / total * 100) : 0;
                    return (
                      <div key={item.key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-500">{item.label}</span>
                          <span className="font-medium text-slate-700">{count} ({pct}%)</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5">
                          <div className={`${item.color} h-1.5 rounded-full`} style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Rep Breakdown */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Rep Performance</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(visitAnalytics.rep_breakdown || []).slice(0, 8).map((rep, i) => (
                    <div key={rep.rep_id} className="flex items-center justify-between text-sm py-1 border-b border-slate-50 last:border-0">
                      <span className="text-slate-700 truncate max-w-[120px]">{rep.rep_name}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-emerald-600" title="Completed">{rep.completed}/{rep.total}</span>
                        {rep.rescheduled > 0 && (
                          <span className="text-amber-600" title="Rescheduled">{rep.rescheduled} resched.</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters Row */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search name, project, phone..."
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  className="pl-9 w-56 h-9 text-sm"
                  data-testid="search-visits"
                />
              </div>
              <Select
                value={filters.rescheduled ? "rescheduled" : (filters.status || "all")}
                onValueChange={(v) => {
                  if (v === "rescheduled") {
                    setFilters(f => ({ ...f, status: "", rescheduled: true }));
                  } else {
                    setFilters(f => ({ ...f, status: v === "all" ? "" : v, rescheduled: false }));
                  }
                }}
              >
                <SelectTrigger className="w-36 h-9 text-sm" data-testid="filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.score || "all"} onValueChange={(v) => setFilters(f => ({ ...f, score: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-32 h-9 text-sm" data-testid="filter-score"><SelectValue placeholder="All Scores" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scores</SelectItem>
                  {SCORE_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {canViewAllLeads && (
                <Select value={filters.sales_rep_id || "all"} onValueChange={(v) => setFilters(f => ({ ...f, sales_rep_id: v === "all" ? "" : v }))}>
                  <SelectTrigger className="w-40 h-9 text-sm" data-testid="filter-assigned-to"><SelectValue placeholder="All Assignees" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Assignees</SelectItem>
                    {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {projects.length > 0 && (
                <Select value={filters.project || "all"} onValueChange={(v) => setFilters(f => ({ ...f, project: v === "all" ? "" : v }))}>
                  <SelectTrigger className="w-40 h-9 text-sm" data-testid="filter-project"><SelectValue placeholder="All Projects" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <DateRangePicker
                dateFrom={dateRange.from}
                dateTo={dateRange.to}
                onChange={(from, to) => setDateRange({ from, to })}
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-slate-500 h-9 text-xs" data-testid="clear-all-filters">
                  <X className="w-3 h-3 mr-1" /> Clear All
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("scheduled_date")}>
                    <div className="flex items-center gap-1">Date <SortIcon col="scheduled_date" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("lead_name")}>
                    <div className="flex items-center gap-1">Lead <SortIcon col="lead_name" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("project")}>
                    <div className="flex items-center gap-1">Project <SortIcon col="project" /></div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Score</TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("rep_name")}>
                    <div className="flex items-center gap-1">Assigned To <SortIcon col="rep_name" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("status")}>
                    <div className="flex items-center gap-1">Status <SortIcon col="status" /></div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Interest</TableHead>
                  <TableHead className="whitespace-nowrap text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(8)].map((_, j) => (
                        <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse w-20"></div></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : sortedVisits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400">
                      <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                      <p>No site visits found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedVisits.map((visit) => {
                    const statusBadge = getStatusBadge(visit.status);
                    const scoreBadge = getScoreBadge(visit.lead_score);
                    return (
                      <TableRow
                        key={visit.visit_id}
                        className="hover:bg-slate-50/50"
                        data-testid={`visit-row-${visit.visit_id}`}
                      >
                        <TableCell className="whitespace-nowrap text-sm">
                          <div className="font-medium text-slate-800">{new Date(visit.scheduled_date).toLocaleDateString()}</div>
                          <div className="text-xs text-slate-400">{new Date(visit.scheduled_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-800 text-sm">{visit.lead_name || "-"}</div>
                          {visit.lead_phone && <div className="text-xs text-slate-400">{visit.lead_phone}</div>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{visit.project || visit.lead_project || "-"}</TableCell>
                        <TableCell>
                          {scoreBadge ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${scoreBadge.color}`}>{scoreBadge.label}</span>
                          ) : <span className="text-xs text-slate-400">-</span>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{visit.rep_name || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusBadge.color}`}>
                              {statusBadge.label}
                            </span>
                            {visit.visit_type === "revisit" && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 font-medium">Revisit</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {visit.interest_level ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              visit.interest_level === "high" ? "bg-emerald-100 text-emerald-700"
                              : visit.interest_level === "medium" ? "bg-amber-100 text-amber-700"
                              : "bg-slate-100 text-slate-600"
                            }`}>{visit.interest_level}</span>
                          ) : <span className="text-xs text-slate-400">-</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {visit.status === "scheduled" && (
                              <Button
                                size="sm"
                                className="h-7 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => {
                                  setSelectedVisit(visit);
                                  setFeedback({ post_visit_feedback: "", interest_level: "", status: "completed" });
                                }}
                                data-testid={`complete-visit-${visit.visit_id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> Complete
                              </Button>
                            )}
                            {visit.status === "scheduled" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedVisit(visit);
                                  setFeedback({ post_visit_feedback: "", interest_level: "", status: "cancelled" });
                                }}
                                data-testid={`cancel-visit-${visit.visit_id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" /> Cancel
                              </Button>
                            )}
                            {(visit.status === "scheduled" || visit.status === "cancelled" || visit.status === "no_show") && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => {
                                  setRescheduleVisit(visit);
                                  setRescheduleForm({ scheduled_date: "", reason: "" });
                                }}
                                data-testid={`reschedule-visit-${visit.visit_id}`}
                              >
                                <Calendar className="w-3 h-3 mr-1" /> Reschedule
                              </Button>
                            )}
                            {visit.status !== "scheduled" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-slate-500"
                                onClick={() => {
                                  setSelectedVisit(visit);
                                  setFeedback({
                                    post_visit_feedback: visit.post_visit_feedback || "",
                                    interest_level: visit.interest_level || "",
                                    status: visit.status,
                                  });
                                }}
                                data-testid={`view-visit-${visit.visit_id}`}
                              >
                                View
                              </Button>
                            )}
                            {visit.status === "completed" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-fuchsia-600 border-fuchsia-200 hover:bg-fuchsia-50"
                                onClick={() => {
                                  setRevisitVisit(visit);
                                  setRevisitForm({ scheduled_date: "", project: visit.project || "", pre_visit_notes: "" });
                                }}
                                data-testid={`schedule-revisit-${visit.visit_id}`}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" /> Revisit
                              </Button>
                            )}
                            {canEdit("edit_site_visits") && visit.status === "scheduled" && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditForm({ ...visit, scheduled_date: visit.scheduled_date?.slice(0, 16) }); setEditOpen(true); }} data-testid={`edit-visit-${visit.visit_id}`}>
                                <Pencil className="w-3.5 h-3.5 text-slate-400" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-amber-600" onClick={() => navigate(`/leads/${visit.lead_id}`)} data-testid={`view-lead-${visit.visit_id}`}>
                              <ExternalLink className="w-3 h-3 mr-1" /> Lead
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Update Visit Dialog */}
        <Dialog open={!!selectedVisit} onOpenChange={(open) => !open && setSelectedVisit(null)}>
          <DialogContent className={feedback.status === "completed" ? "sm:max-w-lg max-h-[85vh] overflow-y-auto" : ""}>
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {feedback.status === "completed" ? "Complete Visit — Post-Visit Analysis" : feedback.status === "cancelled" ? "Cancel Site Visit" : "Visit Details"}
              </DialogTitle>
            </DialogHeader>
            {selectedVisit && (
              <div className="text-sm text-slate-500 mb-2 flex items-center gap-2">
                <span className="font-medium text-slate-700">{selectedVisit.lead_name}</span>
                <span>&middot;</span>
                <span>{selectedVisit.project || selectedVisit.lead_project || "No project"}</span>
                <span>&middot;</span>
                <span>{new Date(selectedVisit.scheduled_date).toLocaleDateString()}</span>
              </div>
            )}
            <div className="space-y-4">
              {feedback.status !== "completed" && (
                <div>
                  <Label>Status</Label>
                  <Select value={feedback.status} onValueChange={(v) => setFeedback({ ...feedback, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(feedback.status === "completed" || selectedVisit?.status === "completed") && (
                <>
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-md">Post-visit analysis is mandatory when completing a visit. Fill in your observations below.</p>
                  <DynamicFormRenderer
                    formType="post_visit_analysis"
                    values={feedback.postVisitData || {}}
                    onChange={(field, value) => setFeedback(prev => ({
                      ...prev,
                      postVisitData: { ...(prev.postVisitData || {}), [field]: value }
                    }))}
                    showGroups={false}
                    gridCols={1}
                  />
                </>
              )}
              {feedback.status !== "completed" && (
                <div>
                  <Label>{feedback.status === "cancelled" ? "Reason for Cancellation" : "Post-Visit Feedback"}</Label>
                  <Textarea
                    placeholder={feedback.status === "cancelled" ? "Why was this visit cancelled?" : "How did the visit go? Any observations, objections, or next steps..."}
                    value={feedback.post_visit_feedback}
                    onChange={(e) => setFeedback({ ...feedback, post_visit_feedback: e.target.value })}
                    rows={4}
                  />
                </div>
              )}
              <Button
                onClick={handleUpdateVisit}
                className={`w-full ${feedback.status === "cancelled" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                data-testid="update-visit-btn"
              >
                {feedback.status === "completed" ? "Complete with Analysis" : feedback.status === "cancelled" ? "Confirm Cancellation" : "Update Visit"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Visit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-md" data-testid="edit-visit-dialog">
            <DialogHeader><DialogTitle>Edit Site Visit</DialogTitle></DialogHeader>
            {editForm && (
              <div className="space-y-3 py-2">
                <div><label className="text-sm font-medium mb-1 block">Scheduled Date</label><Input type="datetime-local" value={editForm.scheduled_date || ""} onChange={e => setEditForm({ ...editForm, scheduled_date: e.target.value })} /></div>
                <div><label className="text-sm font-medium mb-1 block">Project</label><Input value={editForm.project || ""} onChange={e => setEditForm({ ...editForm, project: e.target.value })} /></div>
                <div><label className="text-sm font-medium mb-1 block">Pre-visit Notes</label><Textarea value={editForm.pre_visit_notes || ""} onChange={e => setEditForm({ ...editForm, pre_visit_notes: e.target.value })} rows={3} /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={saveEditVisit} data-testid="save-edit-visit-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reschedule Visit Dialog */}
        <Dialog open={!!rescheduleVisit} onOpenChange={(open) => !open && setRescheduleVisit(null)}>
          <DialogContent className="sm:max-w-md" data-testid="reschedule-visit-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Reschedule Site Visit</DialogTitle>
            </DialogHeader>
            {rescheduleVisit && (
              <div className="text-sm text-slate-500 mb-1">
                <span className="font-medium text-slate-700">{rescheduleVisit.lead_name}</span>
                <span> &middot; {rescheduleVisit.project || rescheduleVisit.lead_project || "No project"}</span>
                <div className="text-xs mt-1">
                  Currently scheduled: <span className="font-medium text-slate-600">{new Date(rescheduleVisit.scheduled_date).toLocaleString()}</span>
                </div>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label>New Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={rescheduleForm.scheduled_date}
                  onChange={(e) => setRescheduleForm(f => ({ ...f, scheduled_date: e.target.value }))}
                  className="mt-1"
                  data-testid="reschedule-date-input"
                />
              </div>
              <div>
                <Label>Reason for Rescheduling</Label>
                <Textarea
                  placeholder="e.g., Client unavailable, weather issue, schedule conflict..."
                  value={rescheduleForm.reason}
                  onChange={(e) => setRescheduleForm(f => ({ ...f, reason: e.target.value }))}
                  rows={3}
                  className="mt-1"
                  data-testid="reschedule-reason-input"
                />
              </div>
              <Button onClick={handleReschedule} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-reschedule-btn">
                <Calendar className="w-4 h-4 mr-2" /> Confirm Reschedule
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Schedule Revisit Dialog */}
        <Dialog open={!!revisitVisit} onOpenChange={(open) => !open && setRevisitVisit(null)}>
          <DialogContent className="sm:max-w-md" data-testid="revisit-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Schedule Revisit</DialogTitle>
            </DialogHeader>
            {revisitVisit && (
              <div className="text-sm text-slate-500 mb-1">
                <span className="font-medium text-slate-700">{revisitVisit.lead_name}</span>
                <span> &middot; {revisitVisit.project || revisitVisit.lead_project || "No project"}</span>
                <div className="text-xs mt-1">
                  Previous visit completed: <span className="font-medium text-slate-600">{new Date(revisitVisit.scheduled_date).toLocaleDateString()}</span>
                </div>
              </div>
            )}
            <div className="space-y-4">
              <DynamicFormRenderer
                formType="revisit"
                values={revisitForm}
                onChange={(field, value) => setRevisitForm(prev => ({ ...prev, [field]: value }))}
                projects={projects}
                showGroups={false}
                gridCols={1}
              />
              <Button onClick={handleScheduleRevisit} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="confirm-revisit-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Schedule Revisit
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SiteVisitsPage;
